/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

import { concatMap, concatMapTo, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { DigitizePointConfigService } from './digitize-point-config.service';
import { MapConfigService } from '../../services/mapConfig.service';
import { DataFormService } from '../../../reports/services/dataform.service';
import { ReportService } from '../../../reports/services/report.service';
import { RestService } from '../../../shared/services/rest.service';
import { MapService } from '../../services/map.service';
import { MapInteractionsService } from '../../services/mapInteractions.service';
import { QueryService } from '../../services/query.service';
import { Actions, Select, Store } from '@ngxs/store';
import { combineLatest, Observable, Subject, zip } from 'rxjs';
import { DigitizableLayer, DigitizedFeature } from './state/digitize-point-config.model';
import { LoadDataForm } from '../../../reports/state/dataForm.actions';
import { LoadDataFormPayload } from '../../../reports/state/dataForm.model';
import { DataFormDialogComponent } from '../../../reports/dataForm/data-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ReportState } from '../../../reports/state/report.state';
import { FeatureReportDefinitionCollection, ReportDefinition } from '../../../reports/state/report.model';
import { LoggerService } from '../../../shared/services/logger.service';
import { SelectionState } from '../../state/selection.state';
import { SelectionFeatures } from '../../state/selection.model';
import { DigitizePointDialogComponent } from './dialog/digitize-point-dialog.component';
import { ClearReports } from '../../../reports/state/report.actions';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({providedIn: 'root'})
export class DigitizePointService {
    potentiallyDigitizableLayers$: Observable<DigitizableLayer[]>;
    digitizableLayers$: Observable<DigitizableLayer[]>;

    @Select(ReportState.featureReports) private featureReports$: Observable<FeatureReportDefinitionCollection>;
    @Select(SelectionState.selection) private selection$: Observable<SelectionFeatures>;

    /**
     * @param logger {LoggerService}
     * @param rest {RestService}
     * @param dialog {MatDialog}
     * @param snackBar {MatSnackBar}
     * @param mapConfig {MapConfigService}
     * @param digitizeConfig {DigitizePointConfigService}
     * @param mapService {MapService}
     * @param queryService {QueryService}
     * @param reportService {ReportService}
     * @param dataFormService {DataFormService}
     * @param mapInteractions {MapInteractionsService}
     * @param actions$ {Actions}
     * @param store {Store}
     */
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private mapConfig: MapConfigService,
        private digitizeConfig: DigitizePointConfigService,
        private mapService: MapService,
        private queryService: QueryService,
        private reportService: ReportService,
        private dataFormService: DataFormService,
        private mapInteractions: MapInteractionsService,
        private actions$: Actions,
        private store: Store
    ) {
        this.potentiallyDigitizableLayers$ = this.digitizeConfig.layers$.pipe(
            map((layers) => layers.filter((l) => l.create))
        );

        this.digitizableLayers$ = combineLatest([
            this.potentiallyDigitizableLayers$,
            this.selection$
        ]).pipe(
            map(([ layers, selection ]) =>
                layers.filter((digitizeLayer) => {
                    const requiredSelectionLayer = digitizeLayer.requireSelection;
                    if (requiredSelectionLayer) {
                        const layerSelection = selection[requiredSelectionLayer];
                        return layerSelection && layerSelection.length === 1;
                    }
                    return true;
                })
            )
        );
    }

    enableDigitize(finishedSignal$?: Subject<void>) {
        let selectedLayer: string;
        this.potentiallyDigitizableLayers$.pipe(take(1)).subscribe((potentiallyDigitizableLayers) => {
            if (potentiallyDigitizableLayers.length > 1) {
                // show choice dialog
                const dialogData = {
                    width: '400px',
                    autoFocus: true,
                    disableClose: true,
                    data: {
                        layers: potentiallyDigitizableLayers
                    }
                };
                const dialogRef = this.dialog.open(DigitizePointDialogComponent, dialogData);
                dialogRef.afterClosed().subscribe((result: string) => {
                    if (result) {
                        selectedLayer = result;
                        this.store.dispatch(new ClearReports({ type: 'feature' }));
                        this.addPoint(selectedLayer, finishedSignal$);
                    }
                });
            } else if (potentiallyDigitizableLayers.length === 0) {
                // Can't do it
                const message = 'No digitizable layers configured. Contact your DashGIS Administrator.';
                this.snackBar.open(message, '', {
                    duration: 5000
                });
                finishedSignal$?.next();
            } else {
                selectedLayer = potentiallyDigitizableLayers[0].layer;
                this.addPoint(selectedLayer, finishedSignal$);
            }
        });
    }


    public canMovePoint$(layerName): Observable<boolean> {
        return this.digitizeConfig.getLayer(layerName).pipe(map((layer) => layer && layer.move));
    }

    public canDeletePoint$(layerName): Observable<boolean> {
        return this.digitizeConfig.getLayer(layerName).pipe(map((layer) => layer && layer.delete));
    }

    public canCreatePoint$(layerName) {
        return this.digitizableLayers$.pipe(
            map((layers) => {
                const layerConfig = layers.find((l) => l.layer === layerName);
                return layerConfig && layerConfig.create;
            })
        );
    }

    public addPoint(layerName: string, finishedSignal$?: Subject<void>): void {
        // ensure layer is visible
        this.mapConfig.showLayer(layerName, true);
        const processFn = this.processSavePoint.bind(this, finishedSignal$);
        setTimeout(() => {
            this.mapInteractions
                .enableDigitizePointInteraction(processFn, layerName);
        }, 0);
    }

    public movePoint(layerName: string, key: any): void {
        const processFn = this.processMovePoint.bind(this);
        this.mapInteractions
            .enableDigitizePointInteraction(processFn, layerName, key);
    }

    public deletePoint(layerName: string, key: any): void {
        const data = {
            key,
            layer: layerName
        };

        const layers = this.mapConfig.getVisibleLayerList(this.mapService.currentScale).map((layer) => layer.name);

        this.rest
            .apiPost(`map/digitize/delete/${ this.mapConfig.mapName }`, data)
            .pipe(
                tap(() => {
                    this.mapService.refreshImageWMSLayers('layers', layers);
                })
            )
            .subscribe(() => {
                this.queryService.clearSelection();
            });
    }

    public processSavePoint(finishedSignal$: Subject<void>, layerName, coordinates) {
        const data: any = {
            coordinates,
            layer: layerName
        };
        const requiredLayer$ = this.digitizeConfig.getLayer(layerName);
        const layerReports$: Observable<ReportDefinition[]> = this.featureReports$.pipe(
            map((reportCollection) => reportCollection[layerName]),
            map((layerReports) => layerReports.filter((r) => r.subType === 'detail' && r.dataFormName)),
        );

        const postResult$ = this.rest.apiPost(`map/digitize/save/${ this.mapConfig.mapName }`, data)
            .pipe(shareReplay());

        const dataFormName$ = layerReports$.pipe(
            map((reports) => {
                if (reports.length === 1) {
                    return reports[0].dataFormName;
                }
            })
        );

        const key$ = postResult$.pipe(
            map((featureInfo): { keyField: string; key: any } => ({
                keyField: (featureInfo as any).keyfield,
                key: (featureInfo as any).key
            }))
        );

        const initialSelection$ = requiredLayer$.pipe(
            filter(dl => !!dl),
            switchMap((layerConfig) => this.queryService.getSingleSelection$(layerConfig.requireSelection))
        );

        zip(dataFormName$, key$, initialSelection$)
            .pipe(
                map(
                    ([ dataFormName, keyObject, initialSelection ]): LoadDataFormPayload => ({
                        name: dataFormName,
                        keys: [ keyObject.key ],
                        initialSelection
                    })
                ),
                switchMap((payload) => this.store.dispatch(new LoadDataForm(payload))),
                concatMapTo(dataFormName$),
                concatMap((dataFormName) =>
                    this.dialog
                        .open(DataFormDialogComponent, {
                            data: dataFormName
                        })
                        .afterClosed()
                ),
                concatMapTo(key$),
                take(1),
                tap((keyInfo) => {
                    this.queryService.selectSingleFeature(layerName, keyInfo.keyField, keyInfo.key);
                }),

                // refresh map
                map(() => this.mapConfig.getVisibleLayerList(this.mapService.currentScale)),
                map((layerList) => layerList.map((layer) => layer.name)),
                tap((layers) => this.mapService.refreshImageWMSLayers('layers', layers)),

                tap(() => {
                    this.mapInteractions.enablePanInteraction();
                })
            ).subscribe(() => finishedSignal$.next());
    }

    public processMovePoint(layerName, key, coordinates) {
        const data: any = {
            coordinates,
            key,
            layer: layerName
        };

        const postResult$ = this.rest
            .apiPost(`map/digitize/move/${ this.mapConfig.mapName }`, data)
            .pipe(take(1), shareReplay());

        postResult$
            .pipe(
                map(() => this.mapConfig.getVisibleLayerList(this.mapService.currentScale)),
                map((layerList) => layerList.map((layer) => layer.name)),
                tap((layers) => this.mapService.refreshImageWMSLayers('layers', layers))
            )
            .subscribe(() => this.mapInteractions.enablePanInteraction());

        postResult$.subscribe((featureInfo: DigitizedFeature) => {
            // select moved feature
            this.queryService.selectSingleFeature(layerName, featureInfo.keyfield, featureInfo.key);
        });
    }

    public getLayerKeyField(layerName): Observable<string> {
        if (layerName) {
            return this.digitizeConfig.getLayer(layerName).pipe(
                filter((layer) => !!layer),
                map((layer: DigitizableLayer) => layer.keyfield)
            );
        }
    }
}
